/**
 * Navigation Utility
 * 
 * Provides functions to map user roles to dashboard routes.
 * This ensures consistent navigation after login across the application.
 */

/**
 * Get dashboard route for a specific role
 * @param {string} role - Frontend role string (e.g., 'super-admin', 'agent')
 * @returns {string} Dashboard route path
 */
export const getRoleDashboardRoute = (role) => {
  const roleRoutes = {
    'super-admin': '/super-admin',
    'admin': '/admin',
    'agent': '/agent',
    'team-lead': '/agent', // Team leads use agent dashboard
    'customer': '/portal',
    'end-user': '/portal',
    'developer': '/developer',
    'billing-admin': '/admin', // Billing admins use admin dashboard
    'auditor': '/admin', // Auditors use admin dashboard
    'integration-agent': '/agent', // Integration agents use agent dashboard
    'ai-orchestrator': '/developer', // AI orchestrators use developer dashboard
  };
  
  return roleRoutes[role] || '/portal'; // Default to portal if role not found
};

/**
 * Navigate to unified home (all users go to /home)
 * @param {string} role - Frontend role string (kept for compatibility)
 * @param {Function} navigate - React Router navigate function
 */
export const navigateToRoleDashboard = (role, navigate) => {
  if (!navigate || typeof navigate !== 'function') {
    console.error('navigate function is required');
    return;
  }
  
  // All users navigate to /home (dynamic menu will show dashboard)
  navigate('/home', { replace: true });
};

/**
 * Check if a route belongs to a specific role
 * @param {string} route - Route path
 * @param {string} role - Frontend role string
 * @returns {boolean} True if route belongs to role
 */
export const isRoleRoute = (route, role) => {
  const roleRoutes = {
    'super-admin': ['/super-admin'],
    'admin': ['/admin'],
    'agent': ['/agent'],
    'team-lead': ['/agent'],
    'customer': ['/portal'],
    'end-user': ['/portal'],
    'developer': ['/developer'],
  };
  
  const routes = roleRoutes[role] || [];
  return routes.some(r => route.startsWith(r));
};

/**
 * Get all routes for a specific role
 * @param {string} role - Frontend role string
 * @returns {string[]} Array of route paths
 */
export const getRoleRoutes = (role) => {
  const roleRoutes = {
    'super-admin': ['/super-admin'],
    'admin': ['/admin'],
    'agent': ['/agent'],
    'team-lead': ['/agent'],
    'customer': ['/portal'],
    'end-user': ['/portal'],
    'developer': ['/developer'],
  };
  
  return roleRoutes[role] || [];
};

export default {
  getRoleDashboardRoute,
  navigateToRoleDashboard,
  isRoleRoute,
  getRoleRoutes,
};

